import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, TextField } from "@mui/material";
import ChangeLanguage from "../../components/ChangeLanguage";
import { ENV } from "../../configs/env";
import { Footer } from "../../components/Footer";
import { MainFooter } from "../../layouts/main/components/footer";
import { sendReceiptEmail } from "../../services/ads/ads.service";
import AuthenticateLayout from "../../components/AuthenticateLayout";

const backendAPIURL: string = process.env.REACT_APP_API_URL || "";
if (!backendAPIURL) {
  throw new Error("REACT_APP_API_URL is not defined");
}
const baseURL: string = process.env.REACT_APP_PUBLIC_BASE_URL || "";

export default function SEReceipt() {

  const navigate = useNavigate();
  const location = useLocation();

  const searchParam = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const receiptID: string | null = searchParam.get("receipt");

  const { t } = useTranslation();

  useEffect(() => {
    if (!receiptID) {
      setErrorMessages("invalidReceipt");
    }
  }, [receiptID, navigate]);

  // const receipt_link = "https://sit-global.sp.smartretail.co/api/receipt?receipt=0X5CtJZ8WMQb5lhSIP4V4ecioe0"
  const receiptLink = `${backendAPIURL}/receipt?receipt=${receiptID}`;

  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState<string>("");

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setErrorMessages("");
    setEmail(e.target.value);
  };

  const handleSendEmail = () => {
    if (email && receiptID) {
      // validate email
      if (!email.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/)) {
        setErrorMessages("invalidEmail");
        return;
      }
      sendReceiptEmail({ receipt_id: receiptID, email: email })
        .then((res) => {
          if (res) {
            setSuccessMessage("successEmailMessage");
          } else {
            setErrorMessages("scanOrContactCustomerService");
          }
        })
        .catch(() => {
          setErrorMessages("scanOrContactCustomerService");
        });
    } else {
      //set error message
      setErrorMessages("pleaseEnterTheEmail");
    }
  };

  const handleViewReceipt = () => {
    if (receiptID) {
      const receiptUrl = `${receiptLink}`;
      window.open(receiptUrl, "_blank");
    } else {
      console.error("receipt ID not found");
    }
  };

  const buttonStyles = {
    borderRadius: 2,
    size: "large",
    mb: 2,
    mt: 1,
    padding: "15px",
    fontSize: "15px",
  };

  const blueButtonStyles = {
    ...buttonStyles,
    backgroundColor: "#4a90e2",
    "&:hover": {
      backgroundColor: "#357ABD",
    },
  };

  const greenButtonStyles = {
    ...buttonStyles,
    backgroundColor: "#27ae60",
    "&:hover": {
      backgroundColor: "#219653",
    },
  };

  const userActionsUi = (
    <Box sx={{ width: "100%", padding: "20px" }}>
      <TextField
        fullWidth
        type="email"
        label={t("emailAddress")}
        value={email}
        onChange={(e) => handleEmailChange(e)}
        margin="normal"
        variant="outlined"
        InputProps={{
          sx: { borderRadius: 2, padding: "2px" },
        }}
        disabled={!receiptID}
      />

      {/* Blue Button */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        sx={blueButtonStyles}
        onClick={handleSendEmail}
        disabled={!email || !receiptID}
      >
        {t("sendReceiptToMyEmail")}
      </Button>
      <Typography variant="body1" color="#b3b3b3" sx={{ mt: 0, textAlign: "center", fontSize: "14px" }}>
        {t("OR")}
      </Typography>
      {/* Green Button */}
      <Button
        fullWidth
        variant="contained"
        size="large"
        sx={greenButtonStyles}
        onClick={handleViewReceipt}
        disabled={!receiptID}
      >
        {t("viewReceipt")}
      </Button>
    </Box>
  );

  const errorMessage = () => {
    return (
      <Box sx={{
        position: "relative",
        top: "10px",
        left: 0,
        right: 0,
        width: "100%",
        paddingLeft: "18px",
        paddingRight: "18px",
        textAlign: "center",
      }}>
        <Typography variant="body1" color="red">
          {errorMessages ? t(errorMessages) : ""}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <AuthenticateLayout
        button={() => !ENV.HIDE_TRANSLATOR && <ChangeLanguage />}
        isLoading={false}
        hideLogo={ENV.HIDE_LOGO || baseURL.includes("sg")}
      >
        <Box
          sx={{
            height: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-start", // Evenly distribute content
            width: "100%",
            position: "relative",
            px: 0.1,
            py: 2,
          }}
        >
          {successMessage ? (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="h6" color="green">
                {t(successMessage)}
              </Typography>
            </Box>
          ) : (
            <>
              {errorMessage && errorMessage()}
              <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-start", mt: 1 }}>
                {userActionsUi}
              </Box>
            </>
          )}
          {!successMessage &&
            <Box sx={{ mt: 2 }}>
              <MainFooter>
                <Footer />
              </MainFooter>
            </Box>
          }
        </Box>
      </AuthenticateLayout>

    </>
  );
}
